/**
 * The dashboard, in the marketing site's visual language.
 *
 * Layout, top to bottom: the KPI strip (five tiles behind hairlines, like the
 * site's speed-stats), then the period's shape (leads and revenue together,
 * with top campaigns beside it), then the detail (when in the day leads
 * arrive, where they end up, where they come from).
 *
 * Decisions worth keeping:
 *
 *   - Leads and revenue share one chart. They are two views of the same days,
 *     and side by side the eye has to carry a date across a gutter to compare
 *     them. Leads are the ink area on the left axis; revenue is the one green
 *     line on the right axis, green because money is the site's single
 *     positive accent.
 *   - Breakdowns are proportion bars, not donuts. Every category is labelled
 *     with its count and share, lengths on one baseline compare at a glance,
 *     and the rainbow is gone: grey steps, with green only for "delivered".
 *   - Switching period keeps the old figures on screen, dimmed, and counts
 *     across to the new ones (see useDashboardStats). No skeleton flash, no
 *     replayed entrance.
 */
import { useId, useMemo, useState } from 'react';
import { Button, Card, Grid, Group, Skeleton, Stack } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import {
  Area, Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { useDashboardStats, type DashboardPeriod, type DashboardStats } from '../api/queries';
import { usePeopleCounts, type PeopleCounts } from '../api/people';
import { CountToggle, useCountUnit } from './people/CountToggle';
import { TOKENS, formatGBP, formatNumber } from '../theme';
import {
  BAR_CURSOR, CHART_INK, CHART_POSITIVE, GRID_PROPS, LINE_CURSOR, MONO_SCALE,
  X_AXIS_PROPS, Y_AXIS_PROPS, chartAnimation, formatCompactGBP, formatCompactNumber,
} from '../charts';
import {
  formatCount, formatHour, formatPercent, formatPounds, formatShare, isoDayLabel, titleCase, ukDayLabel,
} from '../ui/format';
import {
  ChartCard, ChartTooltip, LegendItem, MonoLabel, MonoSegmented, PageHeader,
  ProportionList, StatGrid, StatTile, usePrefersReducedMotion, type ProportionItem,
} from '../ui';
import { useVocab } from '../vocab';

const PERIODS: Array<{ value: DashboardPeriod; label: string; compare: string; bucket: string }> = [
  { value: 'today', label: 'Today', compare: 'vs previous day', bucket: 'Per hour, today' },
  { value: '7d', label: '7 days', compare: 'vs previous 7 days', bucket: 'Per day, last 7 days' },
  { value: '30d', label: '30 days', compare: 'vs previous 30 days', bucket: 'Per day, last 30 days' },
  { value: '90d', label: '90 days', compare: 'vs previous 90 days', bucket: 'Per day, last 90 days' },
];

const MAIN_CHART_H = 280;
const HOURLY_CHART_H = 196;

/* ----------------------------------------------------------------------------
   Formatting
---------------------------------------------------------------------------- */

export { ukDayLabel } from '../ui/format';

/** The hour it is now in the UK, which is the clock the hourly buckets use. */
function londonHour(now = new Date()): number {
  const h = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric', hourCycle: 'h23', timeZone: 'Europe/London',
  }).format(now);
  return Number(h);
}

/* ----------------------------------------------------------------------------
   Page
---------------------------------------------------------------------------- */

export function DashboardPage() {
  const [period, setPeriod] = useState<DashboardPeriod>('7d');
  const {
    data, isPending, error, isPlaceholderData, isFetching, refetch,
  } = useDashboardStats(period);
  const meta = PERIODS.find((p) => p.value === period) ?? PERIODS[1]!;
  // Count people instead of submissions: the headline, the trend and the
  // per-campaign figures. Revenue stays summed over submissions.
  const count = useCountUnit();
  const peopleCounts = usePeopleCounts({ period }, count.unit === 'people');
  const people = count.unit === 'people' ? peopleCounts.data ?? null : undefined;

  const stale = isPlaceholderData;

  return (
    <Stack gap="lg">
      <PageHeader
        title="Home"
        actions={
          <Group gap="md" wrap="wrap">
            {count.available && (
              <CountToggle unit={count.unit} onChange={count.setUnit} updating={people?.updating ?? false} />
            )}
            <MonoSegmented
              value={period}
              onChange={(v) => setPeriod(v as DashboardPeriod)}
              data={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
              aria-label="Period"
            />
          </Group>
        }
      />

      {error && !data ? (
        <ErrorState message={error.message} retrying={isFetching} onRetry={() => void refetch()} />
      ) : isPending || !data ? (
        <DashboardSkeleton />
      ) : (
        <div className={stale ? 'hf-stale' : 'hf-fresh'} aria-busy={stale}>
          <DashboardBody data={data} compare={meta.compare} bucket={meta.bucket} people={people} hourly={period === 'today'} />
        </div>
      )}
    </Stack>
  );
}

/**
 * `people` is undefined when counting submissions, null while the people
 * figures load, and the figures once they have.
 */
function DashboardBody({
  data, compare, bucket, people, hourly = false,
}: {
  data: DashboardStats;
  compare: string;
  bucket: string;
  people?: PeopleCounts | null;
  hourly?: boolean;
}) {
  const leadSpark = useMemo(() => data.lead_performance.map((p) => p.count), [data.lead_performance]);
  const peopleSpark = useMemo(() => (people?.trend ?? []).map((p) => p.people), [people]);
  const revenueSpark = useMemo(() => data.revenue_performance.map((p) => p.revenue), [data.revenue_performance]);
  const v = useVocab();
  // Which money this login may see; the server has already blanked the rest.
  const money = data.money ?? { revenue: true, cost: false, margin: false };
  const ownCost = money.cost && !money.revenue;

  return (
    <Stack gap="md">
      <StatGrid columns={money.revenue ? 5 : 4}>
        {people !== undefined ? (
          <StatTile
            index={0}
            label={`Total ${v.many('person')}`}
            value={people?.people ?? 0}
            format={people ? formatCount : () => '…'}
            caption={people ? `From ${v.count(people.submissions, 'lead')}` : 'Counting'}
            spark={peopleSpark.length >= 2 ? peopleSpark : undefined}
          />
        ) : (
          <StatTile
            index={0}
            label={`Total ${v.many('lead')}`}
            value={data.total_leads}
            format={formatCount}
            delta={data.leads_change}
            deltaLabel={compare}
            spark={leadSpark}
          />
        )}
        <StatTile
          index={1}
          label={v.p.Sold}
          value={data.qualified_leads}
          format={formatCount}
          caption={`${v.Many('lead')} delivered to ${v.a('buyer')}`}
        />
        {money.revenue && (
          <StatTile
            index={2}
            label="Revenue"
            value={data.total_revenue}
            format={formatPounds}
            delta={data.revenue_change}
            deltaLabel={compare}
            spark={revenueSpark}
            sparkTone="positive"
          />
        )}
        <StatTile
          index={3}
          label="Conversion"
          value={data.conversion_rate}
          format={formatPercent}
          delta={data.conversion_change}
          deltaLabel={compare}
          meter={data.conversion_rate / 100}
        />
        <StatTile
          index={4}
          label={`Active ${v.many('campaign')}`}
          value={data.active_campaigns}
          format={formatCount}
          caption={`With ${v.many('lead')} this period`}
        />
      </StatGrid>

      {(money.cost || money.margin) && (
        <StatGrid columns={money.margin ? 2 : 1}>
          {money.cost && (
            <StatTile
              index={5}
              label={ownCost ? 'Earned' : 'Cost'}
              value={data.total_cost}
              format={formatPounds}
              delta={data.cost_change}
              deltaLabel={compare}
              caption={
                ownCost
                  ? `What your ${v.many('lead')} earned this period`
                  : `Paid to ${v.many('supplier')} for this period's ${v.many('lead')}`
              }
            />
          )}
          {money.margin && (
            <StatTile
              index={6}
              label="Margin"
              value={data.margin ?? 0}
              format={formatPounds}
              caption={
                data.margin_pct != null
                  ? `${formatPercent(data.margin_pct)} of revenue`
                  : 'Revenue less cost'
              }
            />
          )}
        </StatGrid>
      )}

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <LeadsRevenueCard data={data} subtitle={bucket} people={hourly ? undefined : people} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <TopCampaignsCard data={data} people={people} />
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 6 }}>
          <HourlyCard data={data} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <DeliveryCard data={data} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <SourcesCard data={data} />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

/* ----------------------------------------------------------------------------
   Rows and revenue
---------------------------------------------------------------------------- */

/** One row per bucket, leads and revenue joined on the bucket label. */
export function mergeSeries(data: DashboardStats) {
  const rows = new Map<string, { label: string; leads: number; revenue: number }>();
  for (const p of data.lead_performance) {
    rows.set(p.date, { label: ukDayLabel(p.date), leads: p.count, revenue: 0 });
  }
  for (const p of data.revenue_performance) {
    const row = rows.get(p.date);
    if (row) row.revenue = p.revenue;
    else rows.set(p.date, { label: ukDayLabel(p.date), leads: 0, revenue: p.revenue });
  }
  return [...rows.values()];
}

/**
 * People per day joined to revenue per day, on the day label both share
 * ("5 Oct"). Today is per hour and has no people per hour, so it is drawn
 * by submissions whichever count is chosen.
 */
export function mergePeopleSeries(data: DashboardStats, people: PeopleCounts) {
  const rows = new Map<string, { label: string; leads: number; revenue: number }>();
  for (const p of people.trend) {
    const label = isoDayLabel(p.date.slice(0, 10));
    rows.set(label, { label, leads: p.people, revenue: 0 });
  }
  for (const p of data.revenue_performance) {
    const label = ukDayLabel(p.date);
    const row = rows.get(label);
    if (row) row.revenue = p.revenue;
    else rows.set(label, { label, leads: 0, revenue: p.revenue });
  }
  return [...rows.values()];
}

function LeadsRevenueCard({
  data, subtitle, people,
}: { data: DashboardStats; subtitle: string; people?: PeopleCounts | null }) {
  const reduced = usePrefersReducedMotion();
  const gradientId = `leads-fill${useId().replace(/:/g, '')}`;
  const rows = useMemo(() => (people ? mergePeopleSeries(data, people) : mergeSeries(data)), [data, people]);
  const empty = rows.length === 0 || rows.every((r) => r.leads === 0 && r.revenue === 0);
  const motion = chartAnimation(reduced);
  const v = useVocab();
  const byPeople = people !== undefined;
  const noun = byPeople ? v.Many('person') : v.Many('lead');
  // A supplier login does not see what its leads sold for.
  const showRevenue = data.money?.revenue ?? true;

  return (
    <ChartCard
      index={5}
      title={showRevenue ? `${noun} and revenue` : noun}
      subtitle={subtitle}
      bodyHeight={MAIN_CHART_H}
      empty={empty}
      emptyText={`No ${v.many('lead')} in this period`}
      right={
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <LegendItem
            color={CHART_INK}
            label={noun}
            value={byPeople ? (people ? formatNumber(people.people) : '…') : formatNumber(data.total_leads)}
          />
          {showRevenue && (
            <LegendItem color={CHART_POSITIVE} label="Revenue" value={formatPounds(data.total_revenue)} line />
          )}
        </div>
      }
    >
      <div role="img" aria-label={`${noun} and revenue, ${subtitle.toLowerCase()}`}>
        <ResponsiveContainer width="100%" height={MAIN_CHART_H}>
          <ComposedChart data={rows} margin={{ top: 6, right: 0, bottom: 0, left: -4 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_INK} stopOpacity={0.13} />
                <stop offset="100%" stopColor={CHART_INK} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" {...X_AXIS_PROPS} />
            <YAxis
              yAxisId="leads"
              {...Y_AXIS_PROPS}
              allowDecimals={false}
              tickFormatter={formatCompactNumber}
            />
            <YAxis
              yAxisId="revenue"
              orientation="right"
              {...Y_AXIS_PROPS}
              tickFormatter={formatCompactGBP}
            />
            <Tooltip
              cursor={LINE_CURSOR}
              isAnimationActive={false}
              content={
                <ChartTooltip
                  names={{ leads: noun, revenue: 'Revenue' }}
                  colors={{ leads: CHART_INK, revenue: CHART_POSITIVE }}
                  formatters={{ leads: formatNumber, revenue: formatGBP }}
                />
              }
            />
            <Area
              yAxisId="leads"
              type="monotone"
              dataKey="leads"
              name={noun}
              stroke={CHART_INK}
              strokeWidth={1.75}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 3.5, stroke: TOKENS.surface, strokeWidth: 2, fill: CHART_INK }}
              {...motion}
            />
            {showRevenue && (
              <Line
                yAxisId="revenue"
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke={CHART_POSITIVE}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 3.5, stroke: TOKENS.surface, strokeWidth: 2, fill: CHART_POSITIVE }}
                {...motion}
                animationBegin={180}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

/* ----------------------------------------------------------------------------
   Hourly distribution
---------------------------------------------------------------------------- */

function HourlyCard({ data }: { data: DashboardStats }) {
  const reduced = usePrefersReducedMotion();
  const nowHour = londonHour();
  const v = useVocab();

  // The server sends only hours that had leads. A day has 24, and a gap is
  // information, so fill them in.
  const rows = useMemo(() => {
    const counts = new Map(data.hourly_distribution.map((h) => [h.hour, h.count]));
    return Array.from({ length: 24 }, (_, hour) => ({
      hour, label: formatHour(hour), count: counts.get(hour) ?? 0,
    }));
  }, [data.hourly_distribution]);

  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const busiest = rows.reduce((best, r) => (r.count > best.count ? r : best));

  return (
    <ChartCard
      index={7}
      title="Hourly distribution"
      subtitle={`When ${v.many('lead')} arrive, UK time`}
      bodyHeight={HOURLY_CHART_H}
      empty={total === 0}
      right={
        total > 0 && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <LegendItem color={CHART_INK} label="Busiest" value={busiest.label} />
            <LegendItem color={CHART_POSITIVE} label="Now" value={formatHour(nowHour)} />
          </div>
        )
      }
    >
      <div role="img" aria-label={`${v.Many('lead')} by hour of day. Busiest hour ${busiest.label}.`}>
        <ResponsiveContainer width="100%" height={HOURLY_CHART_H}>
          <BarChart data={rows} margin={{ top: 6, right: 0, bottom: 0, left: -4 }} barCategoryGap="22%">
            <CartesianGrid {...GRID_PROPS} />
            <XAxis dataKey="label" {...X_AXIS_PROPS} interval="equidistantPreserveStart" minTickGap={14} />
            <YAxis {...Y_AXIS_PROPS} allowDecimals={false} tickFormatter={formatCompactNumber} />
            <Tooltip
              cursor={BAR_CURSOR}
              isAnimationActive={false}
              content={
                <ChartTooltip
                  names={{ count: v.Many('lead') }}
                  colors={{ count: CHART_INK }}
                  formatters={{ count: formatNumber }}
                />
              }
            />
            <Bar dataKey="count" name={v.Many('lead')} radius={[3, 3, 0, 0]} maxBarSize={18} {...chartAnimation(reduced)}>
              {rows.map((r) => (
                <Cell
                  key={r.hour}
                  fill={r.hour === nowHour ? CHART_POSITIVE : CHART_INK}
                  fillOpacity={r.hour === nowHour ? 1 : 0.8}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

/* ----------------------------------------------------------------------------
   Breakdowns
---------------------------------------------------------------------------- */

/**
 * Named counts as proportion rows, largest first, shading down the grey
 * scale. `positive` names the one category painted green. Past `limit` rows
 * the tail folds into "Other", so a long tail cannot push the card's height.
 */
export function breakdownItems(
  rows: Array<{ name: string; count: number }>,
  { positive, blankLabel, limit = 6 }: { positive?: string; blankLabel: string; limit?: number },
): ProportionItem[] {
  const sorted = [...rows].filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
  const total = sorted.reduce((s, r) => s + r.count, 0);
  let shown = sorted;
  if (sorted.length > limit) {
    const head = sorted.slice(0, limit - 1);
    const rest = sorted.slice(limit - 1).reduce((s, r) => s + r.count, 0);
    shown = [...head, { name: '\u0000other', count: rest }];
  }
  let greyStep = 0;
  return shown.map((r) => {
    const isPositive = positive !== undefined && r.name === positive;
    const label = r.name === '\u0000other' ? 'Other' : r.name ? titleCase(r.name) : blankLabel;
    return {
      id: r.name || '\u0000blank',
      label,
      value: formatNumber(r.count),
      share: formatShare(r.count, total),
      fraction: total > 0 ? r.count / total : 0,
      color: isPositive ? CHART_POSITIVE : MONO_SCALE[Math.min(greyStep++, MONO_SCALE.length - 1)],
    };
  });
}

function DeliveryCard({ data }: { data: DashboardStats }) {
  const v = useVocab();
  const items = useMemo(
    () => breakdownItems(data.delivery_breakdown, { positive: 'delivered', blankLabel: 'No status' }),
    [data.delivery_breakdown],
  );
  return (
    <ChartCard index={8} title="Delivery breakdown" subtitle={`Where ${v.many('lead')} ended up`} empty={items.length === 0}>
      <ProportionList items={items} />
    </ChartCard>
  );
}

function SourcesCard({ data }: { data: DashboardStats }) {
  const v = useVocab();
  const items = useMemo(
    () => breakdownItems(data.lead_sources, { blankLabel: 'Unknown' }),
    [data.lead_sources],
  );
  return (
    <ChartCard
      index={9}
      title={v.p.sourceTagMany}
      subtitle={`Where ${v.many('lead')} came from`}
      empty={items.length === 0}
    >
      <ProportionList items={items} />
    </ChartCard>
  );
}

function TopCampaignsCard({ data, people }: { data: DashboardStats; people?: PeopleCounts | null }) {
  const v = useVocab();
  const showRevenue = data.money?.revenue ?? true;
  const byPeople = people !== undefined;
  const items = useMemo<ProportionItem[]>(() => {
    // Counting people, each campaign's figure is its distinct people; the
    // share sold and the revenue stay per submission.
    const perStream = new Map((people?.streams ?? []).map((s) => [s.campaign_id, s.people]));
    const figure = (c: DashboardStats['top_campaigns'][number]) => (byPeople ? perStream.get(c.campaign_id) ?? 0 : c.leads);
    const max = Math.max(0, ...data.top_campaigns.map(figure));
    return data.top_campaigns.map((c) => ({
      id: c.campaign_id,
      label: c.campaign_name || c.campaign_id,
      detail: showRevenue
        ? `${formatShare(c.qualified, c.leads)} ${v.p.sold} · ${formatPounds(c.revenue)}`
        : `${formatShare(c.qualified, c.leads)} ${v.p.sold}`,
      value: byPeople && !people ? '…' : formatNumber(figure(c)),
      fraction: max > 0 ? figure(c) / max : 0,
      tone: 'ink',
    }));
  }, [data.top_campaigns, v, people, byPeople, showRevenue]);

  return (
    <ChartCard
      index={6}
      title={`Top ${v.many('campaign')}`}
      subtitle={`By ${byPeople ? v.many('person') : v.many('lead')}`}
      empty={items.length === 0}
      emptyText={`No ${v.many('campaign')} took ${v.many('lead')}`}
    >
      <ProportionList items={items} rank barHeight={3} />
    </ChartCard>
  );
}

/* ----------------------------------------------------------------------------
   Loading and error
---------------------------------------------------------------------------- */

/** The final layout in grey, so nothing jumps when the figures land. */
function DashboardSkeleton() {
  const block = (h: number, w: number | string = '100%') => <Skeleton height={h} width={w} radius={6} />;
  const card = (bodyH: number) => (
    <Card h="100%" padding={0}>
      <div style={{ padding: '18px 20px 0' }}>
        {block(14, 140)}
        <div style={{ height: 6 }} />
        {block(10, 110)}
      </div>
      <div style={{ padding: '16px 20px 18px' }}>{block(bodyH)}</div>
    </Card>
  );
  return (
    <Stack gap="md" data-testid="dashboard-skeleton" aria-busy aria-label="Loading dashboard">
      <StatGrid columns={5}>
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} style={{ padding: '18px 20px 16px', display: 'grid', gap: 12 }}>
            {block(10, 90)}
            {block(28, 120)}
            {block(16, 150)}
            {block(28)}
          </div>
        ))}
      </StatGrid>
      <Grid gutter="md">
        <Grid.Col span={{ base: 12, md: 8 }}>{card(MAIN_CHART_H)}</Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>{card(MAIN_CHART_H)}</Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>{card(HOURLY_CHART_H)}</Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>{card(HOURLY_CHART_H)}</Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>{card(HOURLY_CHART_H)}</Grid.Col>
      </Grid>
    </Stack>
  );
}

function ErrorState({ message, retrying, onRetry }: { message: string; retrying: boolean; onRetry: () => void }) {
  return (
    <Card padding={0} className="hf-rise">
      <div
        role="alert"
        style={{
          padding: '48px 24px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', textAlign: 'center', gap: 10,
        }}
      >
        <MonoLabel style={{ color: TOKENS.redText }}>Not loaded</MonoLabel>
        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: TOKENS.text }}>
          The dashboard could not load
        </div>
        <div style={{ fontSize: 13, color: TOKENS.textSecondary, maxWidth: 440 }}>{message}</div>
        <Button
          mt={8}
          variant="default"
          leftSection={<IconRefresh size={14} />}
          loading={retrying}
          onClick={onRetry}
        >
          Try again
        </Button>
      </div>
    </Card>
  );
}
